/* eslint-disable max-len */
import { ReactElement, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import {
  isDistrictAdmin,
  isDistrictNonAdmin,
} from 'src/utilities/userUtilities';
import StyledSingleDropdown from '../../../../components/StyledSingleDropdown/StyledSingleDropdown';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  selectSummerActivityFormViewModel,
  selectSummerActivityListItems,
  selectSummerActivityLoadingFormViewModel,
  selectSummerActivityLoadingListItems,
  selectSummerActivitySchoolYears,
} from '../../../../redux/slices/summerActivitySlice';
import {
  fetchSummerActivityFormViewModel,
  fetchSummerActivityListItems,
  fetchSummerActivitySchoolYears,
} from '../../../../redux/thunks/summerActivityThunks';
import { TooltipAnchor } from '../../../../services/app.service';
import { abortPromiseOnUnmount } from '../../../../services/base.service';
import { appRoutePaths } from '../../../../services/route.service';
import { ReactSelectOption } from '../../../../types/ReactSelectOption';
import { SelectOption } from '../../../../types/SelectOption';
import { SummerActivityFormViewModel } from '../../../../types/SummerActivityFormViewModel';
import { SummerActivityListItemModel } from '../../../../types/SummerActivityListItemModel';
import { formatToDateString } from '../../../../utilities/dateUtilities';
import StudentPage from '../StudentPage';
import './SummerActivityList.css';

const SummerActivityList = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const urlSchoolYear = searchParams.get('schoolYear');
  const urlSchool = searchParams.get('school');

  const viewModel: SummerActivityFormViewModel = useAppSelector(
    selectSummerActivityFormViewModel
  );
  const listItems: SummerActivityListItemModel[] = useAppSelector(
    selectSummerActivityListItems
  );
  const schoolYearOptions: SelectOption[] = useAppSelector(
    selectSummerActivitySchoolYears
  );
  const [school, setSchool] = useState<string>(urlSchool || '');
  const [schoolName, setSchoolName] = useState<string>('');
  const [schoolYear, setSchoolYear] = useState<string>(urlSchoolYear || '');

  if (schoolYearOptions && schoolYearOptions.length > 0 && !schoolYear) {
    setSchoolYear(schoolYearOptions[0].value);
  }

  const getSchoolOptions = (): SelectOption[] => {
    return viewModel.schools;
  };

  const schoolOptions = getSchoolOptions();
  const showEditButton = !(isDistrictAdmin() || isDistrictNonAdmin());

  const getSchoolName = (value: string): string => {
    const name = viewModel.schools.filter((x) => x.value === value);
    if (name.length === 1) {
      return name[0].text;
    } else {
      return '';
    }
  };

  const handleSchoolYearChange = async (
    selected: SingleValue<ReactSelectOption>
  ): Promise<void> => {
    /* istanbul ignore next */
    const value = selected?.value || '';
    console.log('handleSchoolYearChange', value);
    console.log('selected', selected);
    if (value)
      dispatch(
        fetchSummerActivityListItems({
          schoolYear: value,
          school,
        })
      );
    setSchoolYear(value);
  };

  const handleSchoolChange = async (
    selected: SingleValue<ReactSelectOption>
  ): Promise<void> => {
    const value = selected?.value || '';
    /* istanbul ignore next */
    if (value || school) {
      dispatch(
        fetchSummerActivityListItems({
          schoolYear,
          school: value,
        })
      );
    }
    setSchool(value);
    setSchoolName(getSchoolName(value));
  };

  const selectedOption = schoolYear
    ? (() => {
        const option = schoolYearOptions?.find(
          (opt) => opt.value === schoolYear
        );
        return option
          ? { value: option.value, label: option.text }
          : { value: schoolYear, label: schoolYear };
      })()
    : schoolYearOptions && schoolYearOptions.length > 0
    ? { value: schoolYearOptions[0].value, label: schoolYearOptions[0].text }
    : { value: '', label: '' };

  const selectedSchoolOption = school
    ? { value: school, label: schoolName }
    : {
        value: '',
        label: '',
      };
  useEffect(() => {
    if (urlSchool && viewModel.schools.length > 0 && !schoolName) {
      const name = getSchoolName(urlSchool);
      if (name) {
        setSchoolName(name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSchool, viewModel.schools]);

  useEffect(() => {
    const defaultFromSchoolYear = '2000';
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const toYear = currentMonth >= 6 ? currentYear + 1 : currentYear;
    const promise = dispatch(
      fetchSummerActivityFormViewModel({
        fromSchoolYear: defaultFromSchoolYear,
        toSchoolYear: toYear.toString(),
      })
    );
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  useEffect(() => {
    let promise: unknown;
    if (schoolYear) {
      promise = dispatch(
        fetchSummerActivityListItems({
          schoolYear: schoolYear,
          school: school,
        })
      );
    }
    return () => {
      abortPromiseOnUnmount(promise);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, schoolYearOptions, schoolYear]);

  useEffect(() => {
    const promise: unknown = dispatch(fetchSummerActivitySchoolYears());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  const handleEdit = (id: string): void => {
    const searchURLParams = new URLSearchParams();
    if (schoolYear) searchURLParams.set('schoolYear', schoolYear);
    if (school) searchURLParams.set('school', school);

    const queryString = searchURLParams.toString();
    const inputUrl = `${appRoutePaths.SummerActivityInput(id)}${
      queryString ? `?${queryString}` : ''
    }`;

    navigate(inputUrl);
  };

  const isFormViewLoading = useAppSelector(
    selectSummerActivityLoadingFormViewModel
  );

  const isListLoading = useAppSelector(selectSummerActivityLoadingListItems);

  return (
    <StudentPage
      pageClass="data-input--student--summer-activity--list"
      isLoading={isFormViewLoading || isListLoading}
      loadingDataId={'summer-activity-list-loader'}
    >
      <div className="content-outer-container">
        <div className="content-inner-container">
          <div className="school-year-selection-container">
            <div>
              <StyledSingleDropdown
                fieldName="schoolYear"
                options={schoolYearOptions ? schoolYearOptions : []}
                onChange={handleSchoolYearChange}
                value={selectedOption}
                labelText="School Year"
                showLabel={true}
                isSearchable={false}
              />
            </div>
            <div className="school-selection">
              <StyledSingleDropdown
                fieldName="school"
                options={schoolOptions ? schoolOptions : []}
                onChange={handleSchoolChange}
                value={selectedSchoolOption}
                class="no-wrap-text"
                labelText="School"
                showLabel={true}
                isSearchable={false}
                isClearable
              />
            </div>
          </div>
          <div className="subheading-and-button-container subheading-container-padding">
            <h4 data-testid={'component-heading-text'}>Summer Activity</h4>
          </div>
          <div className="table-container">
            <table cellSpacing={0} className="page-view-table">
              <thead>
                <tr>
                  <th>MSIS ID</th>
                  <th>Student Name</th>
                  <th>Birthdate</th>
                  <th>Grade Level</th>
                  <th>Reason Description</th>
                  <th>Verified</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listItems?.length > 0 &&
                  listItems.map((summerActivity) => (
                    <tr
                      key={`${summerActivity.id}`}
                      data-testid={summerActivity.id}
                    >
                      <td className="msis-id">{summerActivity.msisId}</td>
                      <td className="student-name">
                        {summerActivity.fullName}
                      </td>
                      <td className="birth-date">
                        {formatToDateString(summerActivity.birthDate)}
                      </td>
                      <td className="grade-level">
                        {summerActivity.gradeLevel}
                      </td>
                      <td className="reason">
                        {summerActivity.reasonDescription}
                      </td>
                      <td className="verified">{summerActivity.verified}</td>
                      <td>
                        <div className="action--column">
                          {showEditButton && (
                            <button
                              data-testid={`edit-button-${summerActivity.id}`}
                              aria-label="Edit"
                              onClick={() => handleEdit(summerActivity.id)}
                              data-tooltip-id={TooltipAnchor}
                              data-tooltip-content={'Edit/View Summer Activity'}
                            >
                              <img src={'/images/icon-edit.svg'} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </StudentPage>
  );
};

export default SummerActivityList;
